import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';
import { type BaseCustomField, CustomFieldTypeName } from '@air/api/types';
import {
  contentCollectionFormSelector,
  setTouchedAction,
  updateContentCollectionClipCustomFieldAction,
} from '@air/feature-content-collection/redux';
import { ContentCollectionCustomFieldsList } from '@air/feature-content-collection/ui';
import { useShortUrlListCustomFields } from '@air/feature-custom-fields/hooks';
import { useShortIdContext } from '@air/provider-short-id';

import { ContentCollectionCustomFieldDateInput } from '~/components/ContentCollection/ContentCollectionCustomFieldDateInput';
import { ContentCollectionCustomFieldMultiSelect } from '~/components/ContentCollection/ContentCollectionCustomFieldMultiSelect';
import { ContentCollectionCustomFieldPlainText } from '~/components/ContentCollection/ContentCollectionCustomFieldPlainText';
import { ContentCollectionCustomFieldSingleSelect } from '~/components/ContentCollection/ContentCollectionCustomFieldSingleSelect';

export type ContentCollectionGalleryAssetCustomFieldsProps = {
  clip: PublicClip;
};

export const ContentCollectionGalleryAssetCustomFields = ({ clip }: ContentCollectionGalleryAssetCustomFieldsProps) => {
  const dispatch = useDispatch();
  const form = useSelector(contentCollectionFormSelector);
  const { shortId } = useShortIdContext();
  const { data, isLoading } = useShortUrlListCustomFields({ shortId: shortId });

  const customFields = useMemo(() => {
    if (!data) return [];

    return data.data.filter((customField) => form?.fields.find((field) => field.customFieldId === customField.id));
  }, [data, form?.fields]);

  const renderCustomField = useCallback(
    ({ customField }: { customField: BaseCustomField }) => {
      switch (customField.type.title) {
        case CustomFieldTypeName.singleSelect: {
          return (
            <ContentCollectionCustomFieldSingleSelect
              clips={[clip]}
              customField={customField}
              onSuccess={({ customFieldWithValues, values }) => {
                dispatch(setTouchedAction({ touched: true }));
                dispatch(
                  updateContentCollectionClipCustomFieldAction({
                    clipId: clip.id,
                    customField: {
                      ...customFieldWithValues,
                      value: values ? values[0] : null,
                    },
                  }),
                );
              }}
              value={clip?.customFields?.find(({ id }) => id === customField.id)?.value}
            />
          );
        }
        case CustomFieldTypeName.multiSelect: {
          return (
            <ContentCollectionCustomFieldMultiSelect
              clips={[clip]}
              customField={customField}
              onSuccess={({ customFieldWithValues, values }) => {
                dispatch(setTouchedAction({ touched: true }));
                dispatch(
                  updateContentCollectionClipCustomFieldAction({
                    clipId: clip.id,
                    customField: { ...customFieldWithValues, values: values ?? null },
                  }),
                );
              }}
              values={clip?.customFields?.find(({ id }) => id === customField.id)?.values}
            />
          );
        }
        case CustomFieldTypeName.plainText: {
          return (
            <ContentCollectionCustomFieldPlainText
              clips={[clip]}
              customField={customField}
              onSuccess={({ customFieldWithValues, value }) => {
                dispatch(setTouchedAction({ touched: true }));
                dispatch(
                  updateContentCollectionClipCustomFieldAction({
                    clipId: clip.id,
                    customField: {
                      ...customFieldWithValues,
                      plainTextValue: value,
                    },
                  }),
                );
              }}
              value={clip?.customFields?.find(({ id }) => id === customField.id)?.plainTextValue}
            />
          );
        }
        case CustomFieldTypeName.date: {
          return (
            <ContentCollectionCustomFieldDateInput
              clips={[clip]}
              customField={customField}
              onSuccess={({ customFieldWithValues, date }) => {
                dispatch(setTouchedAction({ touched: true }));
                dispatch(
                  updateContentCollectionClipCustomFieldAction({
                    clipId: clip.id,
                    customField: {
                      ...customFieldWithValues,
                      dateValue: date,
                    },
                  }),
                );
              }}
              value={clip?.customFields?.find(({ id }) => id === customField.id)?.dateValue}
            />
          );
        }
        default:
          return null;
      }
    },
    [clip, dispatch],
  );

  if (isLoading) return null;

  return (
    <ContentCollectionCustomFieldsList
      renderCustomField={renderCustomField}
      customFields={customFields}
      className="pt-1"
    />
  );
};
